if (!customElements.get('related-products-pro')) {
  class RelatedProductsPro extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rp-track]');
      this.layout = this.dataset.layout;
      this.limit = parseInt(this.dataset.limit, 10) || 10;
      this._bound = false;
      if (this.dataset.recUrl) {
        this.observeThenLoad(() => this.loadRecommendations());
      } else {
        this.setup();
        if (this.dataset.moreUrl) this.observeThenLoad(() => this.loadMore());
      }
    }
    disconnectedCallback() { this.stopAutoplay(); this._io && this._io.disconnect(); }

    observeThenLoad(fn) {
      if (!('IntersectionObserver' in window)) return fn();
      this._io = new IntersectionObserver((entries, io) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect(); fn();
      }, { rootMargin: '0px 0px 700px 0px' });
      this._io.observe(this);
    }

    async fetchItems(url) {
      try {
        const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) return [];
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('[data-rp-track] > .rp-pro__item')).filter(li => !li.querySelector('.rp-pro__skel'));
      } catch (e) { console.error('[related-products-pro]', e); return []; }
    }

    async loadRecommendations() {
      const items = await this.fetchItems(this.dataset.recUrl);
      this.track.innerHTML = '';
      items.forEach(li => this.track.appendChild(li));
      this.classList.remove('is-loading');
      if (!items.length && this.dataset.hideEmpty === 'true') { this.classList.add('is-empty'); return; }
      this.setup();
    }

    async loadMore() {
      const have = this.track.querySelectorAll('.rp-pro__item').length;
      const need = this.limit - have;
      if (need <= 0) return;
      const seen = new Set(Array.from(this.track.querySelectorAll('.rp-pro__item')).map(li => li.dataset.handle));
      const items = await this.fetchItems(this.dataset.moreUrl);
      let added = 0;
      for (const li of items) {
        if (added >= need) break;
        if (seen.has(li.dataset.handle)) continue;
        this.track.appendChild(li); seen.add(li.dataset.handle); added++;
      }
      if (this.layout === 'marquee') this.setupMarquee(true);
      this.updateNav(); this.updateMore();
    }

    setup() {
      if (!this.track.querySelector('.rp-pro__item') && this.dataset.hideEmpty === 'true') { this.classList.add('is-empty'); return; }
      if (this.dataset.shuffle === 'true') this.shuffle();
      this.bindQuickAdd();
      if (this.layout === 'carousel') this.setupCarousel();
      if (this.layout === 'grid') this.setupGrid();
      if (this.layout === 'marquee') this.setupMarquee();
    }

    shuffle() {
      const items = Array.from(this.track.children);
      for (let i = items.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [items[i], items[j]] = [items[j], items[i]]; }
      items.forEach(li => this.track.appendChild(li));
    }

    /* ---- carousel ---- */
    setupCarousel() {
      if (this._bound) return; this._bound = true;
      this.querySelectorAll('.rp-pro__arrow--prev').forEach(b => b.addEventListener('click', () => this.scrollByPage(-1)));
      this.querySelectorAll('.rp-pro__arrow--next').forEach(b => b.addEventListener('click', () => this.scrollByPage(1)));
      this.track.addEventListener('scroll', () => { clearTimeout(this._st); this._st = setTimeout(() => this.updateNav(), 80); }, { passive: true });
      window.addEventListener('resize', () => this.updateNav(), { passive: true });
      this.track.addEventListener('mouseenter', () => this.stopAutoplay());
      this.track.addEventListener('mouseleave', () => this.startAutoplay());
      this.track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      this.updateNav(); this.startAutoplay();
    }
    pageWidth() { return this.track.clientWidth; }
    scrollByPage(dir) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      let next = this.track.scrollLeft + dir * this.pageWidth();
      if (dir > 0 && this.track.scrollLeft >= max - 2) next = 0;           // loop to start
      if (dir < 0 && this.track.scrollLeft <= 2) next = max;               // loop to end
      this.track.scrollTo({ left: next, behavior: 'smooth' });
    }
    updateNav() {
      if (this.layout !== 'carousel') return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const pages = max > 0 ? Math.ceil(this.track.scrollWidth / this.pageWidth()) : 1;
      const cur = max > 0 ? Math.round(this.track.scrollLeft / this.pageWidth()) : 0;
      const dots = this.querySelector('[data-rp-dots]');
      if (dots) {
        if (dots.children.length !== pages) {
          dots.innerHTML = '';
          for (let i = 0; i < pages; i++) {
            const d = document.createElement('button'); d.type = 'button'; d.className = 'rp-pro__dot'; d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            d.addEventListener('click', () => this.track.scrollTo({ left: i * this.pageWidth(), behavior: 'smooth' }));
            dots.appendChild(d);
          }
        }
        Array.from(dots.children).forEach((d, i) => d.classList.toggle('is-active', i === Math.min(cur, pages - 1)));
      }
      const hide = max <= 2;
      this.querySelectorAll('.rp-pro__arrow').forEach(b => { b.style.visibility = hide ? 'hidden' : ''; });
    }
    startAutoplay() {
      const ms = parseInt(this.dataset.autoplay, 10) * 1000;
      if (!ms || this._ap) return;
      this._ap = setInterval(() => this.scrollByPage(1), ms);
    }
    stopAutoplay() { clearInterval(this._ap); this._ap = null; }

    /* ---- grid "show more" ---- */
    setupGrid() {
      const initial = parseInt(this.dataset.gridInitial, 10) || 0;
      this.gridShown = initial > 0 ? initial : Infinity;
      const btn = this.querySelector('[data-rp-more]');
      if (btn && !btn._bound) { btn._bound = true; btn.addEventListener('click', () => { this.gridShown += initial; this.updateMore(); }); }
      this.updateMore();
    }
    updateMore() {
      if (this.layout !== 'grid') return;
      const items = Array.from(this.track.querySelectorAll('.rp-pro__item'));
      items.forEach((li, i) => { li.hidden = i >= this.gridShown; });
      const btn = this.querySelector('[data-rp-more]');
      if (btn) btn.classList.toggle('is-visible', items.length > this.gridShown);
    }

    /* ---- marquee: duplicate items once so the loop is seamless ---- */
    setupMarquee(rebuild) {
      if (rebuild) this.track.querySelectorAll('[data-rp-clone]').forEach(n => n.remove());
      if (this.track.querySelector('[data-rp-clone]')) return;
      const items = Array.from(this.track.children);
      items.forEach(li => { const c = li.cloneNode(true); c.setAttribute('data-rp-clone', ''); c.setAttribute('aria-hidden', 'true'); this.track.appendChild(c); });
      this.bindQuickAdd();
    }

    /* ---- quick add ---- */
    bindQuickAdd() {
      this.querySelectorAll('.rp-pro__btn--add').forEach(btn => {
        if (btn._bound) return; btn._bound = true;
        btn.addEventListener('click', async () => {
          if (btn.classList.contains('is-loading')) return;
          btn.classList.add('is-loading');
          try {
            const drawer = document.querySelector('cart-drawer');
            const sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map(sec => sec.id) : [];
            const res = await fetch((window.routes && routes.cart_add_url) || '/cart/add', {
              method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: btn.dataset.variantId, quantity: 1, sections, sections_url: window.location.pathname })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.description || 'Add to cart failed');
            document.dispatchEvent(new CustomEvent('cart:ajax-update', { detail: { sectionsData: data.sections ? data : null } }));
            if (drawer && this.dataset.openDrawer === 'true' && typeof drawer.open === 'function') drawer.open(btn);
            btn.classList.add('is-added');
            const t = btn.querySelector('.rp-pro__btn-text'); const orig = btn.dataset.defaultText;
            t.textContent = btn.dataset.addedText || 'Added';
            setTimeout(() => { btn.classList.remove('is-added'); t.textContent = orig; }, 2200);
          } catch (e) { console.error('[related-products-pro]', e); }
          finally { btn.classList.remove('is-loading'); }
        });
      });
    }
  }
  customElements.define('related-products-pro', RelatedProductsPro);
}
